import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle, EmptyState } from "@/components/common/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Tags, Plus, Pencil, ShieldCheck, AlertTriangle } from "lucide-react";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 focus-visible:ring-cyan-500";
const CYCLES = ["monthly", "quarterly", "annual", "one_off"];

const emptyForm = () => ({
  code: "", name: "", description: "", product_id: "", billing_cycle: "monthly", currency: "AUD",
  base_price_ex_gst: "", gst_applicable: true, active: true,
  included_vessels: "", included_users: "", included_storage_gb: "",
  overage_per_vessel: "", overage_per_user: "", overage_per_storage_gb: "",
});

export default function CommercialPackages() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(emptyForm());

  const { data: packages } = useQuery({ queryKey: ["commercial-packages"], queryFn: async () => (await api.get("/commercial/packages")).data });
  const { data: products } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });

  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const buildPayload = () => ({
    code: f.code, name: f.name, description: f.description, product_id: f.product_id,
    billing_cycle: f.billing_cycle, currency: f.currency,
    base_price_ex_gst: num(f.base_price_ex_gst), gst_applicable: f.gst_applicable, active: f.active,
    included_vessels: num(f.included_vessels), included_users: num(f.included_users),
    included_storage_gb: num(f.included_storage_gb),
    overage_per_vessel: num(f.overage_per_vessel), overage_per_user: num(f.overage_per_user),
    overage_per_storage_gb: num(f.overage_per_storage_gb),
  });

  const save = useMutation({
    mutationFn: async () => editing
      ? (await api.put(`/commercial/packages/${editing.id}`, buildPayload())).data
      : (await api.post("/commercial/packages", buildPayload())).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["commercial-packages"] }); setOpen(false); toast.success(editing ? "Package updated" : "Package created"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const toggleActive = useMutation({
    mutationFn: async (p) => (await api.post(`/commercial/packages/${p.id}/active?active=${!p.active}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["commercial-packages"] }); toast.success("Status updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const openNew = () => { setEditing(null); setF(emptyForm()); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setF({
      code: p.code || "", name: p.name || "", description: p.description || "", product_id: p.product_id || "",
      billing_cycle: p.billing_cycle || "monthly", currency: p.currency || "AUD",
      base_price_ex_gst: p.base_price_ex_gst ?? "", gst_applicable: p.gst_applicable !== false, active: p.active !== false,
      included_vessels: p.included_vessels ?? "", included_users: p.included_users ?? "", included_storage_gb: p.included_storage_gb ?? "",
      overage_per_vessel: p.overage_per_vessel ?? "", overage_per_user: p.overage_per_user ?? "", overage_per_storage_gb: p.overage_per_storage_gb ?? "",
    });
    setOpen(true);
  };

  const N = (label, k, ph = "not set") => (
    <div><Label className="text-xs text-slate-400">{label}</Label>
      <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className={`mt-1 ${inputCls}`} data-testid={`pkg-${k}`} /></div>
  );

  return (
    <div data-testid="commercial-packages-page">
      <PageHeader title="Commercial Packages" subtitle="Product-scoped commercial definitions: standard price, included allowances and overage rates. Assign to customers on their profile.">
        <Button onClick={openNew} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="new-package-btn"><Plus className="mr-1.5 h-4 w-4" /> New Package</Button>
      </PageHeader>

      <div className="mb-3 flex items-center gap-2 rounded-lg border border-cyan-500/20 bg-cyan-500/5 px-3 py-2 text-xs text-cyan-200">
        <ShieldCheck className="h-4 w-4" /> Restricted to finance administrators. Changing an assigned package affects future billing only — issued invoices are immutable. Every change is audited.
      </div>

      {(!packages || packages.length === 0) ? (
        <EmptyState icon={Tags} title="No commercial packages yet" message="Create a package to define standard pricing, included allowances and overage rates for a product." testId="packages-empty" />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="packages-grid">
          {packages.map((p) => (
            <div key={p.id} className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4" data-testid={`package-card-${p.code}`}>
              <div className="mb-2 flex items-start justify-between">
                <div>
                  <div className="font-mono text-xs text-cyan-300">{p.code}</div>
                  <div className="text-sm font-semibold text-slate-100">{p.name}</div>
                  <div className="text-[11px] text-slate-500">{p.product_name || "—"} · {p.billing_cycle}</div>
                </div>
                <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${p.active ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-600/30 text-slate-400"}`}>{p.active ? "Active" : "Inactive"}</span>
              </div>
              <div className="mb-2 grid grid-cols-3 gap-1 text-center text-[11px]">
                <div className="rounded bg-[#0B1120] py-1.5"><div className="text-slate-500">Vessels</div><div className="text-slate-200">{p.included_vessels ?? "—"}</div></div>
                <div className="rounded bg-[#0B1120] py-1.5"><div className="text-slate-500">Users</div><div className="text-slate-200">{p.included_users ?? "—"}</div></div>
                <div className="rounded bg-[#0B1120] py-1.5"><div className="text-slate-500">Storage GB</div><div className="text-slate-200">{p.included_storage_gb ?? "—"}</div></div>
              </div>
              <div className="mb-3 flex items-center justify-between text-xs">
                <span className="text-slate-500">Base (ex GST)</span>
                <span className="font-mono text-slate-200">{p.base_price_ex_gst ? `$${p.base_price_ex_gst}` : "—"} {p.currency}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-slate-500">{p.assigned_customer_count || 0} customer(s)</span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 border-[#2D3F62] bg-transparent text-xs text-slate-300 hover:bg-[#17233B]" onClick={() => toggleActive.mutate(p)} data-testid={`toggle-active-${p.code}`}>{p.active ? "Deactivate" : "Activate"}</Button>
                  <Button size="sm" variant="outline" className="h-7 border-[#2D3F62] bg-transparent text-xs text-cyan-300 hover:bg-[#17233B]" onClick={() => openEdit(p)} data-testid={`edit-package-${p.code}`}><Pencil className="mr-1 h-3 w-3" /> Edit</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto border-[#1E2D4A] bg-[#111A2E] text-slate-100" data-testid="package-modal">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.code}` : "New commercial package"}</DialogTitle></DialogHeader>
          {editing && editing.assigned_customer_count > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="assigned-warning">
              <AlertTriangle className="h-4 w-4" /> This package is assigned to {editing.assigned_customer_count} customer(s). Changing price, allowances, overage rates or billing cycle affects future billing only — issued invoices are unaffected.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {N("Package code", "code", "e.g. MARINEOS_COMPLIANCE")}
            {N("Name", "name", "Display name")}
            <div className="col-span-2"><Label className="text-xs text-slate-400">Description</Label>
              <Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="pkg-description" /></div>
            <div><Label className="text-xs text-slate-400">Product</Label>
              <Select value={f.product_id} onValueChange={(v) => setF({ ...f, product_id: v })}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="pkg-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">{(products || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-400">Billing cycle</Label>
              <Select value={f.billing_cycle} onValueChange={(v) => setF({ ...f, billing_cycle: v })}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="pkg-cycle"><SelectValue /></SelectTrigger>
                <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">{CYCLES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {N("Base price ex GST ($)", "base_price_ex_gst")}
            {N("Currency", "currency")}
            <div className="col-span-2 mt-1"><SectionTitle>Included allowances (blank = not configured)</SectionTitle></div>
            {N("Included vessels", "included_vessels")}
            {N("Included users", "included_users")}
            {N("Included storage GB", "included_storage_gb")}
            <div className="col-span-2 mt-1"><SectionTitle>Overage rates per additional unit ($ ex GST)</SectionTitle></div>
            {N("Per vessel", "overage_per_vessel")}
            {N("Per user", "overage_per_user")}
            {N("Per storage GB", "overage_per_storage_gb")}
            <div className="col-span-2 flex gap-4 pt-1">
              <label className="flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={f.gst_applicable} onChange={(e) => setF({ ...f, gst_applicable: e.target.checked })} data-testid="pkg-gst" /> GST applicable</label>
              <label className="flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="pkg-active" /> Active</label>
            </div>
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !f.code || !f.name || !f.product_id} className="mt-3 bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="save-package-btn">{editing ? "Save changes" : "Create package"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
